import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faCrosshairs,
  faLink,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faChartLine,
  faExclamationTriangle,
  faClock,
  faSync,
  faProjectDiagram,
  faList,
  faColumns,
  faChartBar,
  faExpand,
  faCompress,
  faTachometerAlt,
  faLightbulb,
  faBorderAll,
  faChartPie
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import blueTeamService from '../services/blueTeamService';
import projectService from '../services/projectService';
import webTargetService from '../services/webTargetService';
import webFindingService from '../services/webFindingService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import ActionDetailModal from '../components/common/ActionDetailModal';
import FindingDetailModal from '../components/common/FindingDetailModal';
import riskScoringService from '../services/riskScoringService';
import CorrelationTimeline from '../components/visualization/CorrelationTimeline';
import EnhancedGapAnalysis from '../components/analytics/EnhancedGapAnalysis';

const PurpleTeamView = () => {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const { projectId: urlProjectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [detectionRate, setDetectionRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [correlating, setCorrelating] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [selectedGapTechnique, setSelectedGapTechnique] = useState(null);
  const [activeTab, setActiveTab] = useState('timeline');
  const [timelineViewMode, setTimelineViewMode] = useState('correlation');
  const [correlationFullscreen, setCorrelationFullscreen] = useState(false);

  // Use URL project ID, or fall back to localStorage
  const projectId = urlProjectId || localStorage.getItem('lastSelectedProject');

  // Escape key exits fullscreen correlation view
  useEffect(() => {
    if (!correlationFullscreen) return;
    const handler = (e) => { if (e.key === 'Escape') setCorrelationFullscreen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [correlationFullscreen]);

  useEffect(() => {
    if (projectId) {
      if (!urlProjectId && projectId) {
        navigate(`/projects/${projectId}/purple-team`, { replace: true });
        return;
      }
      fetchProjectData();
    } else {
      setLoading(false);
    }
  }, [projectId, urlProjectId, navigate]);

  const fetchProjectData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [projectData, timelineData, suggestionsData, detectionData] = await Promise.all([
        projectService.getProject(projectId),
        blueTeamService.getTimeline(projectId),
        blueTeamService.getCorrelationSuggestions(projectId),
        blueTeamService.getDetectionRate(projectId)
      ]);

      setProject(projectData);
      const baseTimeline = Array.isArray(timelineData) ? timelineData : [];
      setSuggestions(Array.isArray(suggestionsData) ? suggestionsData : []);
      setDetectionRate(detectionData);

      // Fetch web findings and merge into timeline (only if web targets exist)
      try {
        const webTargets = await webTargetService.getAll(projectId);
        if (webTargets && webTargets.length > 0) {
          const webFindings = await webFindingService.getAll({ project: projectId });
          if (Array.isArray(webFindings) && webFindings.length > 0) {
            const webTimelineItems = webFindings.map(f => ({
              type: 'web_test',
              id: f._id,
              timestamp: f.createdAt || f.updatedAt,
              title: f.title,
              severity: f.severity,
              wstgTestId: f.wstgTest?.testId,
              endpoint: f.endpoint?.url || `${f.endpoint?.method || ''} ${f.endpoint?.path || ''}`.trim(),
              findingData: f
            }));
            const merged = [...baseTimeline, ...webTimelineItems].sort(
              (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
            );
            setTimeline(merged);
          } else {
            setTimeline(baseTimeline);
          }
        } else {
          setTimeline(baseTimeline);
        }
      } catch {
        // Web data fetch failed silently - use base timeline
        setTimeline(baseTimeline);
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCorrelate = async (findingId, actionId) => {
    try {
      await blueTeamService.correlateFindingWithAction(findingId, actionId);
      fetchProjectData(true);
    } catch (error) {
      console.error('Error correlating:', error);
      const msg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to correlate: ${msg}`);
    }
  };

  const handleUncorrelate = async (findingId) => {
    try {
      await blueTeamService.uncorrelateFinding(findingId);
      fetchProjectData(true);
    } catch (error) {
      console.error('Error uncorrelating:', error);
      const msg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to unlink: ${msg}`);
    }
  };

  const handleBulkCorrelate = async () => {
    setCorrelating(true);
    try {
      const result = await blueTeamService.bulkCorrelate(projectId, 50);
      alert(`Correlated ${result.results.correlated.length} findings`);
      fetchProjectData();
    } catch (error) {
      console.error('Error bulk correlating:', error);
      alert('Failed to bulk correlate');
    } finally {
      setCorrelating(false);
    }
  };

  const getTimelineItemStyle = (item) => {
    if (item.type === 'red_team') {
      return {
        borderColor: item.result === 'successful' ? 'border-red-500' :
                     item.result === 'detected' ? 'border-yellow-500' :
                     item.result === 'blocked' ? 'border-orange-500' : 'border-gray-400',
        bgColor: darkMode
          ? 'bg-gray-700'
          : (item.result === 'successful' ? 'bg-red-50' :
             item.result === 'detected' ? 'bg-yellow-50' :
             item.result === 'blocked' ? 'bg-orange-50' : 'bg-gray-50')
      };
    }
    return {
      borderColor: item.severity === 'critical' ? 'border-purple-500' :
                   item.severity === 'high' ? 'border-blue-500' :
                   'border-cyan-500',
      bgColor: darkMode
        ? 'bg-gray-700'
        : (item.severity === 'critical' ? 'bg-purple-50' :
           item.severity === 'high' ? 'bg-blue-50' :
           'bg-cyan-50')
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-purple-600" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <FontAwesomeIcon icon={faProjectDiagram} className={`text-6xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
        <h2 className={`text-xl font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('purpleTeam.noProjectSelected')}</h2>
        <p className={`mb-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {t('purpleTeam.selectProjectPrompt')}
        </p>
        <Link
          to="/projects"
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          {t('nav.projects')}
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', icon: faTachometerAlt, label: t('purpleTeam.tabDashboard') || 'Dashboard' },
    { id: 'timeline', icon: faChartLine, label: t('purpleTeam.tabTimeline') || 'Timeline' },
    { id: 'suggestions', icon: faLightbulb, label: t('purpleTeam.tabSuggestions') || 'Suggestions' },
    { id: 'gap-analysis', icon: faChartPie, label: t('purpleTeam.tabGapAnalysis') || 'Gap Analysis' },
    { id: 'matrix', icon: faBorderAll, label: t('purpleTeam.tabMatrix') || 'Detection Matrix' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('purpleTeam.title')}
        subtitle={<>{t('purpleTeam.subtitle')}{project && <span className="ml-2 text-purple-600">• {project.name}</span>}</>}
        icon={<div className="flex items-center"><span className="w-4 h-4 bg-red-500 rounded-full"></span><span className="w-4 h-4 bg-blue-500 rounded-full -ml-2"></span></div>}
      >
        <Link
          to={`/projects/${projectId}`}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <FontAwesomeIcon icon={faCrosshairs} className="mr-2" />
          {t('nav.redTeam')}
        </Link>
        <Link
          to={`/projects/${projectId}/blue-team`}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
          {t('nav.blueTeam')}
        </Link>
      </PageHeader>

      {/* Tab Navigation */}
      <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-purple-500 text-purple-600'
                : darkMode
                  ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="mr-2" />
            {tab.label}
            {tab.id === 'suggestions' && suggestions.length > 0 && (
              <span className="ml-2 bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                {suggestions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ====== TAB: DASHBOARD ====== */}
      {activeTab === 'dashboard' && (
        <>
          {/* Auto-Correlate Button */}
          <div className={`rounded-lg shadow p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('purpleTeam.autoCorrelation')}</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('purpleTeam.autoCorrelationDesc')}</p>
              </div>
              <button
                onClick={handleBulkCorrelate}
                disabled={correlating}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {correlating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('purpleTeam.correlating')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="mr-2" />
                    {t('purpleTeam.autoCorrelate')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Detection Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-100 text-sm">{t('purpleTeam.redTeamActions')}</p>
                  <p className="text-3xl font-bold">{detectionRate?.totalActions || 0}</p>
                </div>
                <FontAwesomeIcon icon={faCrosshairs} className="text-4xl text-red-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">{t('purpleTeam.blueTeamDetections')}</p>
                  <p className="text-3xl font-bold">{detectionRate?.detectedByResult || 0}</p>
                </div>
                <FontAwesomeIcon icon={faShieldAlt} className="text-4xl text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">{t('purpleTeam.detectionRate')}</p>
                  <p className="text-3xl font-bold">{detectionRate?.detectedByResultRate || 0}%</p>
                </div>
                <FontAwesomeIcon icon={faChartLine} className="text-4xl text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">{t('purpleTeam.correlated')}</p>
                  <p className="text-3xl font-bold">{detectionRate?.matchedFindings || 0}</p>
                </div>
                <FontAwesomeIcon icon={faLink} className="text-4xl text-purple-200" />
              </div>
            </div>
          </div>

          {/* Avg Time to Detect */}
          {detectionRate?.avgTimeToDetect && (
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FontAwesomeIcon icon={faClock} className="text-2xl text-yellow-500 mr-4" />
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('purpleTeam.avgTimeToDetect')}</p>
                    <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {detectionRate.avgTimeToDetectFormatted}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('purpleTeam.mttd')}</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('purpleTeam.basedOnCorrelated').replace('{{count}}', detectionRate.matchedFindings)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Undetected Techniques Warning */}
          {detectionRate?.undetectedTechniques?.length > 0 && (
            <div className={`border-l-4 border-red-500 p-6 rounded-lg ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
              <div className="flex items-start">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 text-xl mr-4 mt-1" />
                <div>
                  <h3 className={`text-lg font-semibold ${darkMode ? 'text-red-400' : 'text-red-800'}`}>{t('purpleTeam.detectionGapsIdentified')}</h3>
                  <p className={`text-sm mb-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {t('purpleTeam.detectionGapsDesc')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detectionRate.undetectedTechniques.map(tech => (
                      <span
                        key={tech.id}
                        className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm border border-red-200 cursor-pointer hover:ring-2 hover:ring-red-400 transition-all"
                        onClick={() => setSelectedGapTechnique(tech)}
                      >
                        {tech.id} - {tech.name}
                        <span className="text-red-500 ml-1">({tech.tactic})</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Engagement Statistics */}
          {detectionRate && (
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${darkMode ? 'text-white' : ''}`}>
                <FontAwesomeIcon icon={faChartBar} className="mr-2 text-purple-500" />
                {t('purpleTeam.engagementStatistics') || 'Engagement Statistics'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-red-50 border-red-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-red-600'}`}>
                    <FontAwesomeIcon icon={faCrosshairs} className="mr-1" />{t('purpleTeam.redTeamActions')}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{detectionRate.totalActions || 0}</div>
                </div>
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-red-50 border-red-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-red-600'}`}>
                    {t('purpleTeam.successfulAttacks') || 'Successful'}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {timeline.filter(i => i.type === 'red_team' && i.result === 'successful').length}
                  </div>
                </div>
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-red-50 border-red-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-red-600'}`}>
                    {t('purpleTeam.blockedActions') || 'Blocked'}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    {timeline.filter(i => i.type === 'red_team' && i.result === 'blocked').length}
                  </div>
                </div>
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-blue-600'}`}>
                    <FontAwesomeIcon icon={faShieldAlt} className="mr-1" />{t('purpleTeam.blueTeamDetections')}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{detectionRate.detectedByResult || 0}</div>
                </div>
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-purple-50 border-purple-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-purple-600'}`}>
                    <FontAwesomeIcon icon={faLink} className="mr-1" />{t('purpleTeam.correlated')}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{detectionRate.matchedFindings || 0}</div>
                </div>
                <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-green-50 border-green-200'}`}>
                  <div className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-green-600'}`}>
                    {t('purpleTeam.detectionRate')}
                  </div>
                  <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{detectionRate.detectedByResultRate || 0}%</div>
                </div>
              </div>
              {/* Result distribution bar */}
              {detectionRate.totalActions > 0 && (() => {
                const successful = timeline.filter(i => i.type === 'red_team' && i.result === 'successful').length;
                const detected = timeline.filter(i => i.type === 'red_team' && i.result === 'detected').length;
                const blocked = timeline.filter(i => i.type === 'red_team' && i.result === 'blocked').length;
                const other = (detectionRate.totalActions || 0) - successful - detected - blocked;
                const total = detectionRate.totalActions || 1;
                return (
                  <div className="mt-4">
                    <div className={`text-xs font-medium mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {t('purpleTeam.resultDistribution') || 'Result Distribution'}
                    </div>
                    <div className="flex h-4 rounded-full overflow-hidden">
                      {successful > 0 && <div style={{ width: `${(successful / total) * 100}%` }} className="bg-green-500" title={`Successful: ${successful}`} />}
                      {detected > 0 && <div style={{ width: `${(detected / total) * 100}%` }} className="bg-yellow-500" title={`Detected: ${detected}`} />}
                      {blocked > 0 && <div style={{ width: `${(blocked / total) * 100}%` }} className="bg-red-500" title={`Blocked: ${blocked}`} />}
                      {other > 0 && <div style={{ width: `${(other / total) * 100}%` }} className="bg-gray-400" title={`Other: ${other}`} />}
                    </div>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs">
                      <span className="flex items-center"><span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>Successful ({successful})</span>
                      <span className="flex items-center"><span className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></span>Detected ({detected})</span>
                      <span className="flex items-center"><span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>Blocked ({blocked})</span>
                      {other > 0 && <span className="flex items-center"><span className="w-2 h-2 bg-gray-400 rounded-full mr-1"></span>Other ({other})</span>}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* ====== TAB: TIMELINE ====== */}
      {activeTab === 'timeline' && (
        <div className={`rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'} ${
          correlationFullscreen ? 'fixed inset-0 z-40 rounded-none overflow-auto' : ''
        }`}>
          <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : ''}`}>
                  <FontAwesomeIcon icon={faChartLine} className={`mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  {t('purpleTeam.combinedTimeline')}
                </h3>
                {!correlationFullscreen && (
                  <div className={`flex items-center mt-2 space-x-4 text-sm ${darkMode ? 'text-gray-400' : ''}`}>
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-red-500 rounded-full mr-1"></span>
                      {t('purpleTeam.redTeamAction')}
                    </span>
                    <span className="flex items-center">
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-1"></span>
                      {t('purpleTeam.blueTeamDetection')}
                    </span>
                    <span className="flex items-center">
                      <FontAwesomeIcon icon={faLink} className="text-purple-500 mr-1" />
                      {t('purpleTeam.correlated')}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                  <button
                    onClick={() => setTimelineViewMode('list')}
                    className={`px-3 py-1.5 text-sm flex items-center ${
                      timelineViewMode === 'list'
                        ? 'bg-purple-600 text-white'
                        : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={faList} className="mr-1" />
                    {t('purpleTeam.listView') || 'List'}
                  </button>
                  <button
                    onClick={() => setTimelineViewMode('correlation')}
                    className={`px-3 py-1.5 text-sm flex items-center ${
                      timelineViewMode === 'correlation'
                        ? 'bg-purple-600 text-white'
                        : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FontAwesomeIcon icon={faColumns} className="mr-1" />
                    {t('purpleTeam.correlationView') || 'Correlation'}
                  </button>
                </div>
                {/* Fullscreen toggle */}
                {timelineViewMode === 'correlation' && (
                  <button
                    onClick={() => setCorrelationFullscreen(f => !f)}
                    className={`px-2.5 py-1.5 rounded-lg text-sm ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                    title={correlationFullscreen ? (t('purpleTeam.exitFullscreen') || 'Exit fullscreen') : (t('purpleTeam.fullscreen') || 'Fullscreen')}
                  >
                    <FontAwesomeIcon icon={correlationFullscreen ? faCompress : faExpand} />
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="p-6">
            {timelineViewMode === 'list' ? (
              /* LIST VIEW */
              <div className="space-y-4">
                {timeline.map((item, index) => {
                  const style = getTimelineItemStyle(item);
                  return (
                    <div
                      key={`${item.type}-${item.id}-${index}`}
                      className={`relative pl-8 border-l-4 ${style.borderColor} cursor-pointer`}
                      onClick={() => {
                        if (item.type === 'red_team' && item.actionData) {
                          setSelectedAction(item.actionData);
                        } else if (item.type === 'blue_team' && item.findingData) {
                          setSelectedFinding(item.findingData);
                        }
                      }}
                    >
                      <div className={`absolute left-0 top-0 w-4 h-4 -ml-2 rounded-full ${
                        item.type === 'red_team' ? 'bg-red-500' : 'bg-blue-500'
                      }`}></div>

                      <div className={`p-4 rounded-lg ${style.bgColor} hover:ring-2 hover:ring-purple-400 transition-all`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center mb-1">
                              <span className={`px-2 py-1 text-xs rounded mr-2 ${
                                item.type === 'red_team' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {item.type === 'red_team' ? 'RED TEAM' : 'BLUE TEAM'}
                              </span>
                              <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.title}</span>
                              {item.relatedAction && (
                                <FontAwesomeIcon icon={faLink} className="text-purple-500 ml-2" title={t('purpleTeam.correlated')} />
                              )}
                              {item.type === 'red_team' && item.actionData && (() => {
                                try {
                                  const risk = riskScoringService.calculateActionRisk(item.actionData, item.actionData.targets || []);
                                  if (risk.score > 0) {
                                    const riskColor = risk.level === 'Critical' ? 'bg-red-100 text-red-700 border-red-300' :
                                      risk.level === 'High' ? 'bg-orange-100 text-orange-700 border-orange-300' :
                                      risk.level === 'Medium' ? 'bg-yellow-100 text-yellow-700 border-yellow-300' :
                                      'bg-green-100 text-green-700 border-green-300';
                                    return (
                                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full border font-medium ${riskColor}`}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-[10px]" />
                                        {risk.score}/10
                                      </span>
                                    );
                                  }
                                  return null;
                                } catch { return null; }
                              })()}
                            </div>
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {item.technique && <span className="mr-3">{item.technique.id} - {item.technique.name}</span>}
                              {item.operator && <span className="mr-3">by {item.operator}</span>}
                              {item.source && <span className="capitalize">via {item.source.replace(/_/g, ' ')}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {format(new Date(item.timestamp), 'MMM d, HH:mm:ss')}
                            </div>
                            {item.type === 'red_team' && (
                              <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                                item.result === 'successful' ? 'bg-red-100 text-red-800' :
                                item.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                                item.result === 'blocked' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>{item.result}</span>
                            )}
                            {item.type === 'blue_team' && item.severity && (
                              <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                                item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                                item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>{item.severity}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* CORRELATION VIEW - D3 Dual-Lane Timeline */
              <CorrelationTimeline
                timeline={timeline}
                suggestions={suggestions}
                onCorrelate={handleCorrelate}
                onUncorrelate={handleUncorrelate}
                onActionClick={(a) => setSelectedAction(a)}
                onFindingClick={(f) => setSelectedFinding(f)}
                darkMode={darkMode}
                t={t}
                fullscreen={correlationFullscreen}
                onToggleFullscreen={() => setCorrelationFullscreen(f => !f)}
              />
            )}
            {timeline.length === 0 && (
              <div className={`text-center py-8 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {t('purpleTeam.noTimelineData')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====== TAB: SUGGESTIONS ====== */}
      {activeTab === 'suggestions' && (
        <>
          {/* Auto-Correlate Button */}
          <div className={`rounded-lg shadow p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{t('purpleTeam.autoCorrelation')}</h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('purpleTeam.autoCorrelationDesc')}</p>
              </div>
              <button
                onClick={handleBulkCorrelate}
                disabled={correlating}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {correlating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    {t('purpleTeam.correlating')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="mr-2" />
                    {t('purpleTeam.autoCorrelate')}
                  </>
                )}
              </button>
            </div>
          </div>

          {suggestions.length > 0 ? (
            <div className={`rounded-lg shadow ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-lg font-semibold flex items-center ${darkMode ? 'text-white' : ''}`}>
                  <FontAwesomeIcon icon={faLink} className="mr-2 text-purple-500" />
                  {t('purpleTeam.correlationSuggestions')}
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('purpleTeam.correlationSuggestionsDesc')}
                </p>
              </div>
              <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {suggestions.map(item => (
                  <div key={item.finding._id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs mr-2">
                            DETECTION
                          </span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.finding.title}</span>
                          <span className={`ml-2 px-2 py-1 text-xs rounded ${
                            item.finding.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            item.finding.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.finding.severity}
                          </span>
                        </div>
                        <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.finding.suspectedTechnique?.id && (
                            <span className="mr-3">
                              {t('blueTeam.technique')}: {item.finding.suspectedTechnique.id}
                            </span>
                          )}
                          <span>
                            {t('blueTeam.detected')}: {format(new Date(item.finding.detectionTimestamp), 'MMM d, HH:mm')}
                          </span>
                        </div>

                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('purpleTeam.suggestedMatches')}</p>
                          <div className="space-y-2">
                            {item.suggestions.map((suggestion, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center justify-between p-2 rounded border ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}
                              >
                                <div className="flex items-center">
                                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs mr-2">
                                    ACTION
                                  </span>
                                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : ''}`}>{suggestion.action.title}</span>
                                  {suggestion.action.mitreAttackTechnique && (
                                    <span className={`ml-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      ({suggestion.action.mitreAttackTechnique.id})
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-3">
                                  <span className={`px-2 py-1 text-xs rounded ${
                                    suggestion.score >= 70 ? 'bg-green-100 text-green-800' :
                                    suggestion.score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {suggestion.score}% {t('purpleTeam.match')}
                                  </span>
                                  <button
                                    onClick={() => handleCorrelate(item.finding._id, suggestion.action._id)}
                                    className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                                  >
                                    <FontAwesomeIcon icon={faLink} className="mr-1" />
                                    {t('purpleTeam.link')}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          {item.suggestions[0]?.reasons && (
                            <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <span className="font-medium">{t('purpleTeam.why')}</span>{' '}
                              {item.suggestions[0].reasons.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`rounded-lg shadow p-12 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-500 mb-4" />
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('purpleTeam.noSuggestions') || 'No Suggestions'}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('purpleTeam.noSuggestionsDesc') || 'All findings have been correlated or there are no matches to suggest.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* ====== TAB: GAP ANALYSIS ====== */}
      {activeTab === 'gap-analysis' && (
        <>
          <EnhancedGapAnalysis
            actions={timeline.filter(item => item.type === 'action').map(item => item.data)}
            findings={timeline.filter(item => item.type === 'finding').map(item => item.data)}
            detectionData={detectionRate}
            onExport={() => {
              console.log('Export gap analysis');
              // TODO: Implement export functionality
            }}
          />
        </>
      )}

      {/* ====== TAB: DETECTION MATRIX ====== */}
      {activeTab === 'matrix' && (
        <>
          {detectionRate?.techniqueDetection?.length > 0 ? (
            <div className={`rounded-lg shadow p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : ''}`}>{t('purpleTeam.techniqueDetectionMatrix')}</h3>
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('blueTeam.technique')}
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('blueTeam.tactic')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('purpleTeam.attempts')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('purpleTeam.detected')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('purpleTeam.detectionRateCol')}
                      </th>
                      <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('purpleTeam.statusCol')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {detectionRate.techniqueDetection.map(tech => (
                      <tr key={tech.id} className={tech.detected === 0 ? (darkMode ? 'bg-red-900/20' : 'bg-red-50') : ''}>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{tech.id}</span>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{tech.name}</p>
                        </td>
                        <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {tech.tactic}
                        </td>
                        <td className={`px-4 py-3 text-center font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                          {tech.total}
                        </td>
                        <td className={`px-4 py-3 text-center font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                          {tech.detected}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center">
                            <div className={`w-16 rounded-full h-2 mr-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <div
                                className={`h-2 rounded-full ${
                                  tech.detectionRate >= 75 ? 'bg-green-500' :
                                  tech.detectionRate >= 50 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${tech.detectionRate}%` }}
                              />
                            </div>
                            <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>{tech.detectionRate}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {tech.detected === 0 ? (
                            <span className="inline-flex items-center text-red-600">
                              <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                              {t('purpleTeam.gap')}
                            </span>
                          ) : tech.detectionRate >= 75 ? (
                            <span className="inline-flex items-center text-green-600">
                              <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                              {t('purpleTeam.good')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-yellow-600">
                              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                              {t('purpleTeam.improve')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className={`rounded-lg shadow p-12 text-center ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <FontAwesomeIcon icon={faBorderAll} className={`text-5xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('purpleTeam.noMatrixData') || 'No Detection Data'}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('purpleTeam.noMatrixDataDesc') || 'Add red team actions and blue team findings to see the detection matrix.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Detection Gap Technique Modal */}
      {selectedGapTechnique && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedGapTechnique(null)}>
          <div
            className={`rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-start justify-between p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {selectedGapTechnique.id} - {selectedGapTechnique.name}
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('blueTeam.tactic')}: {selectedGapTechnique.tactic}
                </p>
              </div>
              <button onClick={() => setSelectedGapTechnique(null)} className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
            <div className="p-6">
              <p className={`text-sm mb-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                {t('purpleTeam.detectionGapsDesc')}
              </p>
              <h4 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('purpleTeam.redTeamActions')} ({t('purpleTeam.attempts')})
              </h4>
              <div className="space-y-2">
                {timeline
                  .filter(item => item.type === 'red_team' && item.technique?.id === selectedGapTechnique.id)
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border cursor-pointer hover:ring-2 hover:ring-purple-400 transition-all ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                      onClick={() => {
                        if (item.actionData) {
                          setSelectedGapTechnique(null);
                          setSelectedAction(item.actionData);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs mr-2">ACTION</span>
                          <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            item.result === 'successful' ? 'bg-red-100 text-red-800' :
                            item.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                            item.result === 'blocked' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>{item.result}</span>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {format(new Date(item.timestamp), 'MMM d, HH:mm')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                {timeline.filter(item => item.type === 'red_team' && item.technique?.id === selectedGapTechnique.id).length === 0 && (
                  <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{t('common.noResults')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
      {/* Finding Detail Modal */}
      <FindingDetailModal
        finding={selectedFinding}
        isOpen={!!selectedFinding}
        onClose={() => setSelectedFinding(null)}
        projectId={projectId}
      />
    </div>
  );
};

export default PurpleTeamView;
